import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Upload, CheckCircle2 } from 'lucide-react';
import { useApi } from '../context/ApiContext';
import { getRequiredDocsFor } from '../utils/settings';

function StepIndicator({ step }) {
  const steps = ['Application', 'Profile Photo', 'Documents'];
  return (
    <div className="flex items-center justify-center gap-4 mb-6">
      {steps.map((label, i) => {
        const idx = i + 1;
        const active = step === idx;
        const done = step > idx;
        return (
          <div key={label} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${done ? 'bg-green-500 text-white' : active ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-700'}`}>
              {done ? <CheckCircle2 className="w-5 h-5"/> : idx}
            </div>
            <span className={`text-sm ${active ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{label}</span>
            {i < steps.length - 1 && <div className="w-8 h-px bg-gray-300" />}
          </div>
        );
      })}
    </div>
  );
}

export default function TenantApplicationWizard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const slug = params.get('property') || '';
  const unitId = params.get('unit') || '';
  const { fetchProperty, submitApplicationPublic, uploadPublicFile, applicationUploadDocuments } = useApi();

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    moveInDate: '',
    notes: '',
  });
  const [profilePhoto, setProfilePhoto] = useState({ file: null, preview: '' });
  const [documents, setDocuments] = useState([]);

  const requiredDocs = useMemo(() => getRequiredDocsFor(slug), [slug]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        if (!slug) throw new Error('Missing property reference');
        const p = await fetchProperty(slug);
        if (active) setProperty(p || null);
      } catch (e) {
        if (active) setError(e?.message || 'Failed to load property');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [slug, fetchProperty]);

  function onChange(e) {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  }

  function handlePhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setProfilePhoto({ file, preview: url });
  }

  function handleDocChange(docName, file) {
    setDocuments((prev) => {
      const others = prev.filter((d) => d.name !== docName);
      const item = file
        ? { name: docName, fileName: file.name, file, preview: URL.createObjectURL(file) }
        : { name: docName, fileName: '', file: null, preview: '' };
      return [...others, item];
    });
  }

  function next() {
    setStep((s) => Math.min(3, s + 1));
  }
  function prev() {
    setStep((s) => Math.max(1, s - 1));
  }

  async function submit() {
    if (!form.fullName || !form.email || !form.phone) {
      alert('Please complete the application form.');
      setStep(1);
      return;
    }
    const missing = (requiredDocs || []).filter((name) => !documents.find((d) => d.name === name && d.fileName));
    if (missing.length) {
      alert(`Please upload required documents: ${missing.join(', ')}`);
      setStep(3);
      return;
    }

    try {
      const payload = {
        propertySlug: slug,
        unitId: unitId || undefined,
        fullName: form.fullName,
        email: form.email,
        phone: form.phone,
        message: form.notes,
        moveInDate: form.moveInDate || undefined,
      };
      const record = await submitApplicationPublic(payload);

      // Upload files to /api/uploads (if any)
      const uploads = [];
      // Profile photo as a document entry
      if (profilePhoto.file) {
        try {
          const up = await uploadPublicFile(profilePhoto.file, { folder: `applications/${record.id}` });
          uploads.push({ name: 'Profile Photo', url: up.url, path: up.path, fileName: profilePhoto.file.name, mimeType: up.mimeType });
        } catch (err) {
          console.warn('Photo upload failed:', err);
        }
      }
      // Required documents
      for (const d of documents) {
        if (d.file) {
          try {
            const up = await uploadPublicFile(d.file, { folder: `applications/${record.id}` });
            uploads.push({ name: d.name, url: up.url, path: up.path, fileName: d.file.name, mimeType: up.mimeType });
          } catch (err) {
            console.warn('Doc upload failed:', d.name, err);
          }
        }
      }
      if (uploads.length) {
        try {
          await applicationUploadDocuments({ appId: record.id, documents: uploads });
        } catch (err) {
          console.warn('Saving documents failed:', err);
        }
      }

      alert('Application submitted. You are now marked as an Applicant.');
      navigate(`/properties/${encodeURIComponent(slug)}`, { state: { applicationId: record.id } });
    } catch (e) {
      alert(e?.message || 'Failed to submit application');
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }
  if (error || !property) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-700 mb-4">{error || 'Property not found'}</p>
          <button onClick={() => navigate('/properties')} className="px-4 py-2 border rounded">Back to properties</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-5 h-5" /> Back
        </button>

        <div className="bg-white rounded-xl shadow p-6">
          <div className="mb-4">
            <div className="text-xs text-orange-600 font-semibold">APPLY TO RENT</div>
            <h1 className="text-2xl font-bold">{property?.name || 'Selected Property'}</h1>
            <p className="text-sm text-gray-600">Unit: {unitId || 'Not specified'} • Location: {property?.location}</p>
          </div>

          <StepIndicator step={step} />

          {step === 1 && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Full Name</label>
                  <input name="fullName" value={form.fullName} onChange={onChange} className="w-full border rounded px-3 py-2" placeholder="Your full name" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <input name="phone" value={form.phone} onChange={onChange} className="w-full border rounded px-3 py-2" placeholder="+27 .." />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input type="email" name="email" value={form.email} onChange={onChange} className="w-full border rounded px-3 py-2" placeholder="you@example.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Desired Move-in Date</label>
                  <input type="date" name="moveInDate" value={form.moveInDate} onChange={onChange} className="w-full border rounded px-3 py-2" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea name="notes" value={form.notes} onChange={onChange} className="w-full border rounded px-3 py-2" rows={3} placeholder="Anything we should know?" />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <label className="block text-sm font-medium">Upload Profile Photo</label>
              {profilePhoto.preview ? (
                <div className="flex items-center gap-4">
                  <img src={profilePhoto.preview} alt="Profile" className="w-20 h-20 rounded-full object-cover border" />
                  <button className="px-3 py-2 border rounded" onClick={() => setProfilePhoto({ file:null, preview:'' })}>Remove</button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 border-2 border-dashed rounded px-4 py-10 text-gray-600 cursor-pointer">
                  <Upload className="w-5 h-5" />
                  <span>Select image</span>
                  <input type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
                </label>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-700 mb-2">Required Documents (configurable in Admin)</div>
                <div className="space-y-3">
                  {(requiredDocs || []).map((name) => {
                    const current = documents.find((d) => d.name === name);
                    return (
                      <div key={name} className="flex items-center justify-between gap-4 border rounded p-3">
                        <div>
                          <div className="font-medium">{name}</div>
                          <div className="text-xs text-gray-600">{current?.fileName || 'No file uploaded'}</div>
                        </div>
                        <label className="px-3 py-2 border rounded cursor-pointer text-sm">
                          Upload
                          <input type="file" className="hidden" onChange={(e) => handleDocChange(name, e.target.files?.[0])} />
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between">
            <button onClick={prev} disabled={step === 1} className="px-4 py-2 border rounded disabled:opacity-50">Back</button>
            {step < 3 ? (
              <button onClick={next} className="px-4 py-2 bg-orange-500 text-white rounded">Next</button>
            ) : (
              <button onClick={submit} className="px-4 py-2 bg-orange-600 text-white rounded">Submit Application</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
